import React, { createContext, useReducer } from "react";
import reducer from "./reducers";
import { actions } from "./action";
import { TypeCommon } from "models/common";
import { get } from "api/base";
import { LOCAL_STORAGE_KEY, USER_GROUP, USER_ROLE_KOJI, SPECIAL_ORG_CODES, USER_ROLE } from "constants/common.constant";
import { ROUTERS } from "constants/routes.constant";

export const customContext = createContext<TypeCommon>({});

const CustomProvider: React.FC<TypeCommon> = ({ children }) => {
  const initialState = {
    listOrg: [],
    userServices: [],
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const { listOrg, userServices } = state;

  const getList = async (): Promise<void> => {
    const res = await get("public", "/organization/list", {
      keys: ["id", "code", "name", "is_summit", "services"],
    });
    const listOrgs = res?.data?.map((item: TypeCommon) => {
      return {
        code: item?.code,
        isSummit: item?.is_summit,
        value: item?.id,
        name: item?.name,
        label: item?.is_summit ? `${item?.code} ${item?.name}` : item?.name,
        services: item?.services,
        hasUserRoot: item?.has_user_root,
      };
    });
    dispatch({
      type: actions.GET_LIST_ORGANIZATION,
      payload: listOrgs,
    });
  };
  
  const getTaskCodeConfig = async (): Promise<string> => {
    const res = await get("task", "/task/code-config", {
      type_code: "APPROVER_TASK",
      code: "APPROVER",
    });
    if (res?.data) {
      const escapedJson = JSON.parse(res?.data[0]?.value);
      return JSON.stringify(escapedJson);
    }
    return '';
  };

  const getUserDetail = async (): Promise<void> => {
    let listService: TypeCommon[] = [];
    const res = await get("user", "/user/detail");
    if (
      Array.isArray(res?.data?.owner_services) &&
      res?.data?.owner_services?.length > 0
    ) {
      const list = res?.data?.owner_services ?? [];
      listService = [...list];
    }
    if (Array.isArray(res?.data?.services) && res?.data?.services?.length > 0) {
      const list = res?.data?.services ?? [];
      list?.forEach((item: TypeCommon) => {
        const service = listService?.find(
          (serviceItem: TypeCommon) => serviceItem?.id === item?.id,
        );
        if (!service) {
          listService?.push(item);
        }
      });
    }
    localStorage.setItem(
      LOCAL_STORAGE_KEY.USER_INFO,
      JSON.stringify({
        ...res?.data,
        first_name: res?.data?.first_name,
        last_name: res?.data?.last_name,
        first_name_yomi: res?.data?.first_name_yomi,
        last_name_yomi: res?.data?.last_name_yomi,
        group: res?.data?.group,
        role: res?.data?.role,
        email: res?.data?.email,
        phone_number: res?.data?.phone_number,
        sub: res?.data?.user_id,
        id: res?.data?.id,
        organization_id: res?.data.organization_id,
        organization_code: res?.data.organization?.code,
        organization_name: res?.data.organization?.name,
        username: res?.data?.username,
        is_tokubai: res.data?.is_tokubai,
        owner_services: res?.data?.owner_services,
      }),
    );
    listService?.forEach((item: TypeCommon) => {
      //denshi
      if (item?.url?.includes(ROUTERS.DENSHI)) {
        localStorage.setItem(LOCAL_STORAGE_KEY.DENSHI, "1");
      }
      //bidashboard
      if (item?.url?.includes(ROUTERS.BIDASHBOARD)) {
        localStorage.setItem(LOCAL_STORAGE_KEY.BIDASHBOARD, "1");
      }
      //forecast
      if (item?.url?.includes(ROUTERS.FORECAST)) {
        localStorage.setItem(LOCAL_STORAGE_KEY.FORECAST, "1");
      }
      //intranet
      if (item?.url?.includes(ROUTERS.INTRANET)) {
        localStorage.setItem(LOCAL_STORAGE_KEY.INTRANET, "1");
      }
      //ecm
      if (item?.url?.includes(ROUTERS.ECM)) {
        localStorage.setItem(LOCAL_STORAGE_KEY.ECM, "1");
      }
      //nebiki
      if (item?.url?.includes(ROUTERS.NEBIKI)) {
        localStorage.setItem(LOCAL_STORAGE_KEY.NEBIKI, "1");
      }
      //bakery
      if (item?.url?.includes(ROUTERS.BAKERY)) {
        localStorage.setItem(LOCAL_STORAGE_KEY.BAKERY, "1");
      }
      //tokubai
      if (item?.url?.includes(ROUTERS.TOKUBAI)) {
        localStorage.setItem(LOCAL_STORAGE_KEY.TOKUBAI, "2");
        // serviceTokubaiId = item?.id;
      }
    });

    // define role koji
    localStorage.setItem(LOCAL_STORAGE_KEY.USER_ROLE_KOJI, checkUserRoleKoji(res?.data));

    // get approver task by config
    const approverTask = await getTaskCodeConfig();
    localStorage.setItem(LOCAL_STORAGE_KEY.APPROVER_TASK, approverTask);

    dispatch({
      type: actions.GET_USER_DETAIL,
      payload: listService,
    });
  };

  const checkUserRoleKoji = (userInfo: TypeCommon): string => {
    if (!userInfo?.role) {
      return '';
    }
    if (userInfo?.group === USER_GROUP.PLATFORM_ADMIN) {
      return USER_ROLE_KOJI.PLATFORM_ADMIN;
    }
    if (userInfo?.owner_services?.some((owner_services: TypeCommon) => owner_services?.type === "dashboard")) {
      return USER_ROLE_KOJI.APPROVER_HONBU_SERVICE_OWNER;
    }
    if (userInfo?.organization) {
      const orgCode = parseInt(userInfo?.organization?.code);
      if ("020" === userInfo?.organization?.code) {
        return USER_ROLE_KOJI.APPROVER_HONBU_020;
      }
      if (userInfo?.organization?.is_summit && orgCode > 100) {
        if (userInfo?.role === USER_ROLE.MANAGER) {
          return USER_ROLE_KOJI.APPROVER_TENPO;
        } 
        return USER_ROLE_KOJI.APPROVER_NORMAL;
      }
      if (userInfo?.organization?.is_summit && orgCode <= 100) {
        return USER_ROLE_KOJI.APPROVER_HONBU;
      }
    }

    if (!userInfo?.organization?.is_summit) {
      if (SPECIAL_ORG_CODES.includes(userInfo?.organization?.code)) {
        return USER_ROLE_KOJI.CREATER_PARENT;
      } 
      return USER_ROLE_KOJI.CREATER;
    }
    return '';
  };
  
  const resetUser = (): void => {
    dispatch({
      type: actions.RESET_USER_DATA,
      payload: [],
    });
  };

  return (
    <customContext.Provider
      value={{
        listOrg,
        getList,
        userServices,
        getUserDetail,
        resetUser,
      }}
    >
      {children}
    </customContext.Provider>
  );
};

export default CustomProvider;
